import { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';

import { useBusinessCategoryStore } from '@/stores/businessCategoryStore';
import {
  fetchMyDemandDetail,
  createDemandDraft,
  updateDemandDraft,
  submitDemandAudit,
} from '@/service/api/demand';
import { Select, DatePicker, Toast } from '@/components/common';
import { useAuth } from '@/hooks/useAuth';
import { CURRENCY_OPTIONS } from '@/utils/currency';
import { uploadFile } from '@/utils/upload';

const getTodayString = (): string => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const getTomorrowString = (): string => {
  const now = new Date();
  now.setDate(now.getDate() + 1);
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const BUDGET_TYPE_OPTIONS: { label: string; value: Api.Demand.BudgetType }[] = [
  { label: '预算范围', value: 'RANGE' },
  { label: '固定预算', value: 'FIXED' },
  { label: '面议', value: 'NEGOTIABLE' },
];

export interface DemandAttachmentItem {
  id: string;
  file_name: string;
  file_size?: number;
  url?: string;
}

/** 格式化文件大小 */
function formatFileSize(bytes?: number): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function PostDemandPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('id');

  const { user, isEnterprise } = useAuth();
  const isEnterpriseVerified = isEnterprise && user?.verified;
  const { categories, fetchCategories, loading: categoriesLoading } = useBusinessCategoryStore();

  const [formData, setFormData] = useState({
    title: '',
    types: [] as string[],
    budgetType: 'RANGE' as Api.Demand.BudgetType,
    budgetMin: '',
    budgetMax: '',
    budgetAmount: '',
    currency: 'CNY',
    deadline: '',
    expectedDelivery: '',
    description: '',
    requirements: '',
  });
  const [attachments, setAttachments] = useState<DemandAttachmentItem[]>([]);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const attachmentInputRef = useRef<HTMLInputElement>(null);

  const [loadingDetail, setLoadingDetail] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' | 'warning' } | null>(null);

  const showToast = (message: string, type: 'success' | 'error' | 'info' | 'warning' = 'info') => {
    setToast({ message, type });
  };

  const handleAttachmentUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    if (attachments.length + files.length > 5) {
      showToast('需求附件最多支持上传 5 个文件', 'warning');
      return;
    }

    setUploadingAttachment(true);
    try {
      const newItems: DemandAttachmentItem[] = [];
      for (const file of files) {
        const uploaded = await uploadFile(file, 'DEMAND', 'public-when-published');
        const assetId = uploaded.upload_asset_id || uploaded.asset_id;
        if (assetId) {
          newItems.push({
            id: String(assetId),
            file_name: uploaded.file_name || file.name,
            file_size: uploaded.file_size || file.size,
            url: uploaded.file_url,
          });
        }
      }
      setAttachments((prev) => [...prev, ...newItems]);
      showToast('附件上传成功', 'success');
    } catch (err: any) {
      console.error('附件上传失败:', err);
      showToast(err?.message || '附件上传失败，请重试', 'error');
    } finally {
      setUploadingAttachment(false);
      if (attachmentInputRef.current) {
        attachmentInputRef.current.value = '';
      }
    }
  };

  const handleRemoveAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // 当处于继续编辑状态 (存在 editId) 时，通过 /my/demands/{id} 接口读回详情
  useEffect(() => {
    if (!editId) return;
    setLoadingDetail(true);
    fetchMyDemandDetail(editId)
      .then((res) => {
        if (!res) return;
        let minStr = '';
        let maxStr = '';
        if (res.budget_min) {
          minStr = Number(res.budget_min).toString();
        } else if (res.budget_amount) {
          minStr = Number(res.budget_amount).toString();
        }

        if (res.budget_max) {
          maxStr = Number(res.budget_max).toString();
        }

        // 若未能解析完整，尝试解析 budget_display（如 "10000-10000 CNY" 或 "10000-20000 CNY"）
        if ((!minStr || !maxStr) && res.budget_display) {
          const matches = res.budget_display.match(/\d+(?:\.\d+)?/g);
          if (matches && matches.length >= 2) {
            if (!minStr) minStr = parseFloat(matches[0]).toString();
            if (!maxStr) maxStr = parseFloat(matches[1]).toString();
          } else if (matches && matches.length === 1) {
            const valStr = parseFloat(matches[0]).toString();
            if (!minStr) minStr = valStr;
            if (!maxStr) maxStr = valStr;
          }
        }

        let deadlineStr = '';
        if (res.bid_deadline_at) {
          const d = new Date(res.bid_deadline_at);
          if (!isNaN(d.getTime())) {
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            deadlineStr = `${y}-${m}-${day}`;
          }
        }

        let expectedDeliveryStr = '';
        if (res.expected_delivery_at) {
          const d = new Date(res.expected_delivery_at);
          if (!isNaN(d.getTime())) {
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            expectedDeliveryStr = `${y}-${m}-${day}`;
          }
        }

        const reqStr = Array.isArray(res.undertaking_requirements)
          ? res.undertaking_requirements.join('\n')
          : (res.undertaking_requirements as any) || '';

        // 读回附件
        if (Array.isArray(res.attachments) && res.attachments.length > 0) {
          const mapped = res.attachments
            .map((att) => ({
              id: String(att.upload_asset_id || ''),
              file_name: att.display_name || att.file_name || '附件',
              file_size: att.file_size,
              url: att.access_url,
            }))
            .filter((item) => Boolean(item.id));
          setAttachments(mapped);
        } else if (Array.isArray(res.attachment_ids) && res.attachment_ids.length > 0) {
          setAttachments(
            res.attachment_ids.map((id) => ({
              id: String(id),
              file_name: `附件_${String(id).slice(0, 6)}`,
            }))
          );
        }

        let inferredType: Api.Demand.BudgetType = 'RANGE';
        if (res.budget_type === 'FIXED' || res.budget_type === 'NEGOTIABLE' || res.budget_type === 'RANGE') {
          inferredType = res.budget_type as Api.Demand.BudgetType;
        } else if (res.budget_display && res.budget_display.includes('面议')) {
          inferredType = 'NEGOTIABLE';
        }

        const categoryCodes = Array.isArray(res.category_codes)
          ? res.category_codes
          : res.category_codes
          ? [res.category_codes as unknown as string]
          : [];

        setFormData({
          title: res.title || '',
          types: categoryCodes,
          budgetType: inferredType,
          budgetMin: minStr,
          budgetMax: maxStr,
          budgetAmount: minStr || maxStr,
          currency: (res as any).currency || 'CNY',
          deadline: deadlineStr,
          expectedDelivery: expectedDeliveryStr,
          description: res.description || '',
          requirements: reqStr,
        });
      })
      .catch((err) => {
        console.error('加载需求详情失败:', err);
        showToast('加载需求数据失败，请重试', 'error');
      })
      .finally(() => {
        setLoadingDetail(false);
      });
  }, [editId]);

  const handleBudgetMinBlur = () => {
    if (!formData.budgetMin.trim() || !formData.budgetMax.trim()) return;
    const minVal = parseFloat(formData.budgetMin);
    const maxVal = parseFloat(formData.budgetMax);
    if (!isNaN(minVal) && !isNaN(maxVal) && maxVal < minVal) {
      setFormData((prev) => ({ ...prev, budgetMax: prev.budgetMin }));
    }
  };

  const handleBudgetMaxBlur = () => {
    if (!formData.budgetMin.trim() || !formData.budgetMax.trim()) return;
    const minVal = parseFloat(formData.budgetMin);
    const maxVal = parseFloat(formData.budgetMax);
    if (!isNaN(minVal) && !isNaN(maxVal) && maxVal < minVal) {
      setFormData((prev) => ({ ...prev, budgetMax: prev.budgetMin }));
    }
  };

  const validateForm = () => {
    if (!formData.title.trim()) {
      showToast('请输入需求标题', 'error');
      return false;
    }
    if (!formData.types || formData.types.length === 0) {
      showToast('请至少选择一个需求类型', 'error');
      return false;
    }

    if (formData.budgetType === 'RANGE') {
      if (!formData.budgetMin.trim()) {
        showToast('请输入预算下限', 'error');
        return false;
      }
      if (!formData.budgetMax.trim()) {
        showToast('请输入预算上限', 'error');
        return false;
      }
      const minVal = parseFloat(formData.budgetMin);
      const maxVal = parseFloat(formData.budgetMax);
      if (!isNaN(minVal) && !isNaN(maxVal) && maxVal < minVal) {
        showToast('预算上限不能低于预算下限', 'error');
        return false;
      }
    } else if (formData.budgetType === 'FIXED') {
      if (!formData.budgetAmount.trim() && !formData.budgetMin.trim()) {
        showToast('请输入固定预算金额', 'error');
        return false;
      }
    }

    if (!formData.deadline.trim()) {
      showToast('请选择截止日期', 'error');
      return false;
    }
    if (!formData.description.trim()) {
      showToast('请输入需求描述', 'error');
      return false;
    }
    if (!formData.requirements.trim()) {
      showToast('请输入承接要求', 'error');
      return false;
    }
    return true;
  };

  const buildPayload = (): Api.Demand.DemandRequest => {
    const budget_type: Api.Demand.BudgetType = formData.budgetType || 'RANGE';
    let budget_min: string | undefined;
    let budget_max: string | undefined;
    let budget_amount: string | undefined;

    if (budget_type === 'RANGE') {
      const minVal = formData.budgetMin.trim() ? parseFloat(formData.budgetMin) : undefined;
      const maxVal = formData.budgetMax.trim() ? parseFloat(formData.budgetMax) : undefined;
      if (minVal !== undefined) budget_min = minVal.toString();
      if (maxVal !== undefined) budget_max = maxVal.toString();
    } else if (budget_type === 'FIXED') {
      const amtStr = formData.budgetAmount.trim() || formData.budgetMin.trim();
      const amtVal = amtStr ? parseFloat(amtStr) : undefined;
      if (amtVal !== undefined) budget_amount = amtVal.toString();
    } else if (budget_type === 'NEGOTIABLE') {
      // 面议类型无需传金额
    }

    let bidDeadlineAt: string | undefined;
    if (formData.deadline) {
      const trimmed = formData.deadline.trim();
      if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
        bidDeadlineAt = trimmed;
      } else {
        const d = new Date(trimmed);
        if (!isNaN(d.getTime())) {
          const year = d.getFullYear();
          const month = String(d.getMonth() + 1).padStart(2, '0');
          const day = String(d.getDate()).padStart(2, '0');
          bidDeadlineAt = `${year}-${month}-${day}`;
        }
      }
    }

    let expectedDeliveryAt: string | undefined;
    if (formData.expectedDelivery) {
      const trimmed = formData.expectedDelivery.trim();
      if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
        expectedDeliveryAt = trimmed;
      } else {
        const d = new Date(trimmed);
        if (!isNaN(d.getTime())) {
          const year = d.getFullYear();
          const month = String(d.getMonth() + 1).padStart(2, '0');
          const day = String(d.getDate()).padStart(2, '0');
          expectedDeliveryAt = `${year}-${month}-${day}`;
        }
      }
    }

    const attachmentIds = attachments.map((att) => att.id).filter(Boolean);

    return {
      title: formData.title.trim(),
      category_codes: formData.types,
      description: formData.description.trim(),
      undertaking_requirements: formData.requirements.trim(),
      budget_type,
      budget_min,
      budget_max,
      budget_amount,
      bid_deadline_at: bidDeadlineAt,
      expected_delivery_at: expectedDeliveryAt,
      currency: formData.currency || 'CNY',
      attachments: attachmentIds.length > 0 ? attachmentIds : undefined,
    };
  };

  const handleSaveDraft = async () => {
    if (!validateForm()) return;

    try {
      setIsSavingDraft(true);
      const payload = buildPayload();
      if (editId) {
        await updateDemandDraft(editId, payload);
      } else {
        await createDemandDraft(payload);
      }
      showToast(editId ? '需求更新成功！正在跳转...' : '保存草稿成功！正在跳转...', 'success');
      setTimeout(() => {
        navigate('/my-postings');
      }, 1500);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || '保存草稿失败，请稍后重试';
      showToast(msg, 'error');
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setIsSubmitting(true);
      const payload = buildPayload();
      let targetId = editId;
      if (editId) {
        await updateDemandDraft(editId, payload);
      } else {
        const draftRes = await createDemandDraft(payload);
        targetId = draftRes?.id || null;
      }

      if (targetId) {
        await submitDemandAudit(targetId);
      }
      showToast('需求提交成功，已提交平台审核！正在跳转...', 'success');
      setTimeout(() => {
        navigate('/my-postings');
      }, 1500);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || '发布需求失败，请稍后重试';
      showToast(msg, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navbar />
      <div className="pt-28 sm:pt-32">
        <div className="max-w-3xl mx-auto px-4 md:px-8 py-10">
          <nav className="flex items-center gap-2 text-xs text-foreground-400 mb-8 pl-1 sm:pl-2">
            <Link to="/" className="hover:text-foreground-700 transition-colors cursor-pointer">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/my-postings" className="hover:text-foreground-700 transition-colors cursor-pointer">我的发布</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-700">{editId ? '编辑需求' : '发布需求'}</span>
          </nav>

          <div className="glass-card rounded-2xl p-6 md:p-8">
            {!isEnterpriseVerified ? (
              <div className="py-16 text-center max-w-md mx-auto">
                <div className="w-16 h-16 rounded-2xl bg-amber-100/80 text-amber-600 flex items-center justify-center mx-auto mb-4 text-2xl shadow-sm">
                  <i className="ri-building-4-line"></i>
                </div>
                <h2 className="text-base font-bold text-foreground-950 mb-2">需要完成企业认证</h2>
                <p className="text-xs text-foreground-500 mb-6 leading-relaxed">
                  发布需求与发布招聘功能仅面向通过认证的企业账号开放。完成企业认证后，即可开始发布与管理需求。
                </p>
                <div className="flex items-center justify-center gap-3">
                  <Link
                    to="/demands"
                    className="px-4 py-2.5 rounded-xl border border-gray-300 bg-white text-foreground-700 text-xs font-medium hover:bg-gray-50 transition-all"
                  >
                    返回需求大厅
                  </Link>
                  <Link
                    to="/certification"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary-600 hover:bg-primary-700 text-white text-xs font-semibold transition-all shadow-md shadow-primary-500/20"
                  >
                    <i className="ri-shield-check-line text-sm"></i>
                    前往企业认证
                  </Link>
                </div>
              </div>
            ) : loadingDetail ? (
              <div className="py-16 text-center">
                <div className="inline-block w-8 h-8 border-3 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
                <p className="text-xs text-foreground-400 mt-3">正在加载需求原数据...</p>
              </div>
            ) : (
              <>
                <h1 className="font-heading font-bold text-lg text-foreground-950 mb-6">
                  {editId ? '编辑需求' : '发布需求'}
                </h1>

                <div className="space-y-5">
                  <div>
                    <label className="block text-xs font-medium text-foreground-700 mb-1.5">
                      需求标题 <span className="text-red-500 ml-0.5">*</span>
                    </label>
                    <input type="text" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} placeholder="清晰概括需求内容，如：古装仙侠短剧全案制作" className="w-full px-4 py-3 rounded-lg border border-background-300/60 bg-white text-sm text-foreground-950 placeholder-foreground-400 outline-none focus:border-primary-400/60 transition-all" />
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="block text-xs font-medium text-foreground-700">
                        需求类型 <span className="text-red-500 ml-0.5">*</span>
                        <span className="text-foreground-400 font-normal ml-1">（可多选）</span>
                      </label>
                      {formData.types.length > 0 && (
                        <span className="text-xs text-primary-600 font-medium">
                          已选择 {formData.types.length} 项
                        </span>
                      )}
                    </div>
                    {categoriesLoading ? (
                      <div className="flex items-center gap-2 py-3 text-xs text-foreground-400">
                        <div className="w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
                        类目加载中...
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-2 p-3 rounded-xl bg-background-50/50 border border-background-200/80">
                        {categories.map((cat) => {
                          const isSelected = formData.types.includes(cat.code);
                          return (
                            <button
                              key={cat.code}
                              type="button"
                              onClick={() => {
                                const newTypes = isSelected
                                  ? formData.types.filter((t) => t !== cat.code)
                                  : [...formData.types, cat.code];
                                setFormData({ ...formData, types: newTypes });
                              }}
                              className={`px-3.5 py-2 rounded-lg text-xs font-medium transition-all cursor-pointer flex items-center gap-1.5 ${
                                isSelected
                                  ? 'bg-primary-50 border border-primary-300 text-primary-600 shadow-2xs font-semibold'
                                  : 'bg-white text-foreground-700 border border-background-300/60 hover:border-primary-300/60 hover:bg-primary-50/20'
                              }`}
                            >
                              {isSelected ? (
                                <i className="ri-checkbox-circle-fill text-primary-600 text-sm"></i>
                              ) : (
                                <i className="ri-checkbox-blank-circle-line text-foreground-400 text-sm"></i>
                              )}
                              {cat.name}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-foreground-700 mb-1.5">
                      预算方式 <span className="text-red-500 ml-0.5">*</span>
                    </label>
                    <Select
                      value={formData.budgetType}
                      onChange={(val) => setFormData({ ...formData, budgetType: val as Api.Demand.BudgetType })}
                      options={BUDGET_TYPE_OPTIONS}
                      placeholder="请选择预算方式"
                    />
                  </div>

                  {formData.budgetType === 'RANGE' && (
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-foreground-700 mb-1.5">
                          预算下限 <span className="text-red-500 ml-0.5">*</span>
                        </label>
                        <input
                          type="number"
                          value={formData.budgetMin}
                          onChange={(e) => setFormData({ ...formData, budgetMin: e.target.value })}
                          onBlur={handleBudgetMinBlur}
                          placeholder="如 5000"
                          className="w-full px-4 py-3 rounded-lg border border-background-300/60 bg-white text-sm text-foreground-950 placeholder-foreground-400 outline-none focus:border-primary-400/60 transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-foreground-700 mb-1.5">
                          预算上限 <span className="text-red-500 ml-0.5">*</span>
                        </label>
                        <input
                          type="number"
                          value={formData.budgetMax}
                          onChange={(e) => setFormData({ ...formData, budgetMax: e.target.value })}
                          onBlur={handleBudgetMaxBlur}
                          placeholder="如 15000"
                          className="w-full px-4 py-3 rounded-lg border border-background-300/60 bg-white text-sm text-foreground-950 placeholder-foreground-400 outline-none focus:border-primary-400/60 transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-foreground-700 mb-1.5">
                          结算币种
                        </label>
                        <Select
                          value={formData.currency}
                          onChange={(value) => setFormData({ ...formData, currency: String(value) })}
                          options={CURRENCY_OPTIONS}
                          placeholder="币种"
                        />
                      </div>
                    </div>
                  )}

                  {formData.budgetType === 'FIXED' && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-foreground-700 mb-1.5">
                          固定预算金额 <span className="text-red-500 ml-0.5">*</span>
                        </label>
                        <input type="number" value={formData.budgetAmount || formData.budgetMin} onChange={(e) => setFormData({ ...formData, budgetAmount: e.target.value, budgetMin: e.target.value })} placeholder="如 10000" className="w-full px-4 py-3 rounded-lg border border-background-300/60 bg-white text-sm text-foreground-950 placeholder-foreground-400 outline-none focus:border-primary-400/60 transition-all" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-foreground-700 mb-1.5">
                          结算币种
                        </label>
                        <Select
                          value={formData.currency}
                          onChange={(value) => setFormData({ ...formData, currency: String(value) })}
                          options={CURRENCY_OPTIONS}
                          placeholder="币种"
                        />
                      </div>
                    </div>
                  )}

                  {formData.budgetType === 'NEGOTIABLE' && (
                    <div className="p-4 rounded-xl border border-background-200/80 bg-background-50/50 text-xs text-foreground-600 flex items-center gap-2">
                      <i className="ri-information-line text-primary-600 text-base"></i>
                      当前选定价格面议，具体预算金额将在接洽过程中与服务商协商确定。
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-foreground-700 mb-1.5">
                        招募截止日期 <span className="text-red-500 ml-0.5">*</span>
                      </label>
                      <DatePicker
                        value={formData.deadline}
                        minDate={getTomorrowString()}
                        onChange={(date) => setFormData({ ...formData, deadline: date })}
                        placeholder="选择截止日期"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-foreground-700 mb-1.5">
                        预计交付日期
                      </label>
                      <DatePicker
                        value={formData.expectedDelivery}
                        minDate={formData.deadline || getTomorrowString()}
                        onChange={(date) => setFormData({ ...formData, expectedDelivery: date })}
                        placeholder="选择预计交付日期"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-foreground-700 mb-1.5">
                      需求描述 <span className="text-red-500 ml-0.5">*</span>
                    </label>
                    <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="详细描述需求内容和期望..." rows={5} maxLength={500} className="w-full px-4 py-3 rounded-lg border border-background-300/60 bg-white text-sm text-foreground-950 placeholder-foreground-400 outline-none focus:border-primary-400/60 transition-all resize-none"></textarea>
                    <p className="text-xs text-foreground-400 mt-1 text-right">{formData.description.length}/500</p>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-foreground-700 mb-1.5">
                      承接要求（每行一条） <span className="text-red-500 ml-0.5">*</span>
                    </label>
                    <textarea value={formData.requirements} onChange={(e) => setFormData({ ...formData, requirements: e.target.value })} placeholder="如：&#10;具备古装仙侠短剧成功案例&#10;全流程覆盖：编剧 + 拍摄 + 后期" rows={4} maxLength={300} className="w-full px-4 py-3 rounded-lg border border-background-300/60 bg-white text-sm text-foreground-950 placeholder-foreground-400 outline-none focus:border-primary-400/60 transition-all resize-none"></textarea>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="block text-xs font-medium text-foreground-700">
                        需求附件 <span className="text-foreground-400 font-normal">（选填，最多 5 个文件）</span>
                      </label>
                      {attachments.length < 5 && (
                        <button
                          type="button"
                          disabled={uploadingAttachment}
                          onClick={() => attachmentInputRef.current?.click()}
                          className="inline-flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 font-medium cursor-pointer disabled:opacity-50"
                        >
                          <i className="ri-attachment-2"></i>
                          {uploadingAttachment ? '上传中...' : '添加附件'}
                        </button>
                      )}
                    </div>
                    <input
                      ref={attachmentInputRef}
                      type="file"
                      multiple
                      className="hidden"
                      onChange={handleAttachmentUpload}
                    />
                    {attachments.length > 0 ? (
                      <div className="space-y-2 mt-2">
                        {attachments.map((item, idx) => (
                          <div
                            key={item.id || idx}
                            className="flex items-center justify-between p-2.5 rounded-lg border border-background-200/80 bg-background-50/50 text-xs"
                          >
                            <div className="flex items-center gap-2 min-w-0 pr-2">
                              <i className="ri-file-text-line text-foreground-500 text-sm flex-shrink-0"></i>
                              <span className="truncate text-foreground-800 font-medium">{item.file_name}</span>
                              {item.file_size && (
                                <span className="text-foreground-400 text-[11px] flex-shrink-0">
                                  ({formatFileSize(item.file_size)})
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {item.url && (
                                <a
                                  href={item.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-primary-600 hover:text-primary-700"
                                >
                                  查看
                                </a>
                              )}
                              <button
                                type="button"
                                onClick={() => handleRemoveAttachment(idx)}
                                className="text-foreground-400 hover:text-red-500 transition-colors p-1"
                              >
                                <i className="ri-delete-bin-line"></i>
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div
                        onClick={() => attachmentInputRef.current?.click()}
                        className="border border-dashed border-background-300/80 rounded-lg p-4 text-center cursor-pointer hover:border-primary-400/60 transition-colors"
                      >
                        <i className="ri-upload-cloud-line text-lg text-foreground-400 mb-1 block"></i>
                        <p className="text-xs text-foreground-500">点击上传剧本、制作需求说明或参考文件</p>
                      </div>
                    )}
                  </div>

                  <div className="pt-4 border-t border-background-200/70 flex flex-col sm:flex-row gap-3">
                    <button
                      type="button"
                      onClick={handleSubmit}
                      disabled={isSubmitting || isSavingDraft}
                      className="flex-1 py-3 rounded-lg bg-primary-500 text-foreground-950 text-sm font-semibold hover:bg-primary-400 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {isSubmitting && <div className="w-4 h-4 rounded-full border-2 border-foreground-950/20 border-t-foreground-950 animate-spin"></div>}
                      发布需求
                    </button>
                    <button
                      type="button"
                      onClick={handleSaveDraft}
                      disabled={isSubmitting || isSavingDraft}
                      className="flex-1 py-3 rounded-lg border border-gray-200/60 text-foreground-600 text-sm hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {isSavingDraft && <div className="w-4 h-4 rounded-full border-2 border-foreground-600/20 border-t-foreground-600 animate-spin"></div>}
                      保存草稿
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}